import React, { useState, useEffect } from "react";
import ToastContainer, {
  useToast,
} from "../../../components/common/ToastContainer";
import { sanitizeSearchQuery } from "../../../utils/inputValidation";
import "../../../assets/scss/app.scss";

const Listing = () => {
  const [listingData, setListingData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const { toasts, removeToast, showSuccess, showError, showWarning } =
    useToast();

  useEffect(() => {
    fetchListingData();
  }, []);

  useEffect(() => {
    // Filter data based on search term
    if (searchTerm) {
      const filtered = listingData.filter(
        (item) =>
          item.patient_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.hidn_number?.includes(searchTerm) ||
          item.hid_address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.facility_name?.toLowerCase().includes(searchTerm.toLowerCase()),
      );
      setFilteredData(filtered);
    } else {
      setFilteredData(listingData);
    }
  }, [searchTerm, listingData]);

  const fetchListingData = async () => {
    try {
      setLoading(true);

      const response = await fetch(
        `${process.env.REACT_APP_M2}/abha/user/listing`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.data && result.data.data) {
        setListingData(result.data.data);
        setFilteredData(result.data.data);
        showSuccess(`Successfully loaded ${result.data.data.length} records`);
      } else {
        setError("No data found in response");
        showWarning("No ABHA user data found");
      }
    } catch (err) {
      console.error("Error fetching listing data:", err);
      setError(err.message);
      showError(`Failed to fetch listing data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "N/A";
    return date.toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getHiTypeData = (abhaDetails) => {
    const hiTypes = [];

    if (abhaDetails.DiagnosticReport) {
      hiTypes.push({
        type: "Diagnostic Report",
        count: abhaDetails.DiagnosticReport.count || 0,
        display: abhaDetails.DiagnosticReport.display,
      });
    }

    if (abhaDetails.DischargeSummary) {
      hiTypes.push({
        type: "Discharge Summary",
        count: abhaDetails.DischargeSummary.count || 0,
        display: abhaDetails.DischargeSummary.display,
      });
    }

    if (abhaDetails.Invoice) {
      hiTypes.push({
        type: "Invoice",
        count: abhaDetails.Invoice.count || 0,
        display: abhaDetails.Invoice.display,
      });
    }

    if (abhaDetails.Prescription) {
      hiTypes.push({
        type: "Prescription",
        count: abhaDetails.Prescription.count || 0,
        display: abhaDetails.Prescription.display,
      });
    }

    if (abhaDetails.OPConsultation) {
      hiTypes.push({
        type: "OP Consultation",
        count: abhaDetails.OPConsultation.count || 0,
        display: abhaDetails.OPConsultation.display,
      });
    }

    if (abhaDetails.HealthDocumentRecord) {
      hiTypes.push({
        type: "Health Document",
        count: abhaDetails.HealthDocumentRecord.count || 0,
        display: abhaDetails.HealthDocumentRecord.display,
      });
    }

    if (abhaDetails.WellnessRecord) {
      hiTypes.push({
        type: "Wellness Record",
        count: abhaDetails.WellnessRecord.count || 0,
        display: abhaDetails.WellnessRecord.display,
      });
    }

    if (abhaDetails.ImmunizationRecord) {
      hiTypes.push({
        type: "Immunization",
        count: abhaDetails.ImmunizationRecord.count || 0,
        display: abhaDetails.ImmunizationRecord.display,
      });
    }

    return hiTypes;
  };

  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case "pending":
        return "badge bg-warning text-dark";
      case "completed":
        return "badge bg-success";
      case "active":
        return "badge bg-primary";
      default:
        return "badge bg-secondary";
    }
  };

  if (loading) {
    return (
      <div className="container-fluid">
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "400px" }}
        >
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Loading ABHA User Listing...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid">
        <div className="alert alert-danger m-4">
          <h4>Error Loading Data</h4>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={fetchListingData}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="container-fluid">
        <div className="opd-page">
          {/* Header Section */}
          <div className="row mb-4">
            <div className="col-12">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h2 className="mb-1">ABHA User Listing</h2>
                  <p className="text-muted">
                    Total Records: {filteredData.length}
                    {searchTerm && ` (filtered from ${listingData.length})`}
                  </p>
                </div>
                <button
                  className="customeBtn btnLightBlueBorder"
                  onClick={fetchListingData}
                  disabled={loading}
                >
                  {loading ? "Refreshing..." : "↻ Refresh"}
                </button>
              </div>
            </div>
          </div>

          {/* Search and Filter Section */}
          <div className="opd-filter mb-4">
            <div className="row">
              <div className="col-md-6">
                <div className="feilds">
                  <label className="label">Search</label>
                  <div className="searchBar">
                    <input
                      type="text"
                      placeholder="Search by name, ABHA number, or facility..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(sanitizeSearchQuery(e.target.value))}
                      required
                    />
                    <span className="searchIcon">
                      <i className="fas fa-search"></i>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="OpdBodySection">
            {filteredData.length === 0 ? (
              <div className="text-center py-5">
                <div className="alert alert-info">
                  <h4>No Records Found</h4>
                  <p>
                    {searchTerm
                      ? `No records match your search criteria: "${searchTerm}"`
                      : "No ABHA user records are available at the moment."}
                  </p>
                  {searchTerm && (
                    <button
                      className="btn btn-outline-primary"
                      onClick={() => setSearchTerm("")}
                    >
                      Clear Search
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="row">
                {filteredData.map((item, index) => (
                  <div
                    key={item._id || index}
                    className="col-xl-6 col-lg-12 mb-4"
                  >
                    <div className="card h-100 shadow-sm">
                      <div className="card-header bg-light">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <h5 className="mb-1 text-primary">
                              {item.patient_name}
                            </h5>
                            <small className="text-muted">
                              ABHA: {item.hidn_number} • {item.hid_address}
                            </small>
                          </div>
                          <span className={getStatusBadgeClass(item.status)}>
                            {item.status}
                          </span>
                        </div>
                      </div>

                      <div className="card-body">
                        {/* Patient Basic Info */}
                        <div className="row mb-3">
                          <div className="col-6">
                            <small className="text-muted">Gender:</small>
                            <div className="fw-bold">
                              {item.abha_details?.gender || "N/A"}
                            </div>
                          </div>
                          <div className="col-6">
                            <small className="text-muted">Year of Birth:</small>
                            <div className="fw-bold">
                              {item.abha_details?.yearOfBirth || "N/A"}
                            </div>
                          </div>
                        </div>

                        {/* Facility Info */}
                        <div className="mb-3">
                          <small className="text-muted">Facility:</small>
                          <div className="fw-bold">{item.facility_name}</div>
                          <small className="text-muted">
                            ID: {item.facility_id}
                          </small>
                        </div>

                        {/* Health Information Types */}
                        <div className="mb-3">
                          <small className="text-muted">
                            Health Information Types:
                          </small>
                          <div className="mt-2">
                            {getHiTypeData(item.abha_details).map(
                              (hiType, idx) => (
                                <div
                                  key={idx}
                                  className="d-flex justify-content-between align-items-center mb-2 p-2 bg-light rounded"
                                >
                                  <div>
                                    <span className="fw-bold">
                                      {hiType.type}
                                    </span>
                                    <br />
                                    <small className="text-muted">
                                      {hiType.display}
                                    </small>
                                  </div>
                                  <span className="badge bg-primary">
                                    {hiType.count}
                                  </span>
                                </div>
                              ),
                            )}
                            {getHiTypeData(item.abha_details).length === 0 && (
                              <div className="text-muted fst-italic">
                                No health information records
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Timestamps */}
                        <div className="row">
                          <div className="col-6">
                            <small className="text-muted">Created:</small>
                            <div className="small">
                              {formatDate(item.createdAt)}
                            </div>
                          </div>
                          <div className="col-6">
                            <small className="text-muted">Updated:</small>
                            <div className="small">
                              {formatDate(item.updatedAt)}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="card-footer bg-light">
                        <div className="d-flex justify-content-between align-items-center">
                          <small className="text-muted">
                            Active: {item.is_active ? "Yes" : "No"}
                          </small>
                          {item.version_m2?.last_request_id && (
                            <small className="text-muted">
                              Last Request:{" "}
                              {item.version_m2.last_request_id.slice(-8)}
                            </small>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Toast Container for notifications */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default Listing;
